/**
 * Operation-budget readout, permanently in the header.
 *
 * Operation cost is the product's real capacity limit — on Explorer access the whole
 * platform affords roughly seven pushes per day (02-platform-constraints.md). A paying
 * agency should be able to see that ceiling at all times, not only at the moment of
 * confirming a push.
 */
import { useOpsBudget } from '@/api/hooks'
import { formatNumber } from '@/lib/format'
import { Badge, Cap, Meter } from './ui'

export function OpsBudgetWidget() {
  const { data, isLoading, isError } = useOpsBudget()
  const google = data?.find((entry) => entry.platform === 'google')

  if (isLoading) {
    return <span className="text-[11.5px] text-mist-500">reading ops budget…</span>
  }
  if (isError || !google) {
    return (
      <span data-testid="ops-budget-widget" className="text-[11.5px] text-mist-500">
        ops budget unavailable
      </span>
    )
  }

  const ratio = google.daily_ceiling === 0 ? 0 : google.consumed_operations / google.daily_ceiling
  const tone = ratio > 0.9 ? 'danger' : ratio > 0.7 ? 'warn' : 'ok'

  return (
    <div data-testid="ops-budget-widget" className="min-w-[210px]">
      <div className="flex items-baseline justify-between gap-2">
        <Cap>Google ops today</Cap>
        <span className="num text-[11.5px] text-mist-300">
          {formatNumber(google.consumed_operations)}
          <span className="text-mist-500"> / {formatNumber(google.daily_ceiling)}</span>
        </span>
      </div>
      <div className="mt-1">
        <Meter
          value={google.consumed_operations}
          max={google.daily_ceiling}
          tone={tone}
          label={`Google operations consumed today: ${google.consumed_operations} of ${google.daily_ceiling}`}
          testId="ops-budget-meter"
        />
      </div>
      <div className="mt-1 flex items-center gap-1.5">
        <Badge tone={google.access_level === 'explorer' ? 'warn' : 'neutral'}>
          {google.access_level} access
        </Badge>
        <span className="num text-[11px] text-mist-500">
          {formatNumber(google.available_operations)} left
        </span>
      </div>
    </div>
  )
}
